import { InjectiveEvmV1Query } from '@injectivelabs/core-proto-ts';
import { UnspecifiedErrorCode, grpcErrorCodeToErrorCode, GrpcUnaryRequestException, } from '@injectivelabs/exceptions';
import { ChainModule } from '../types/index.js';
import BaseGrpcConsumer from '../../base/BaseGrpcConsumer.js';
import { ChainGrpcEvmTransformer } from '../transformers/index.js';
/**
 * @category Chain Grpc API
 */
export class ChainGrpcEvmApi extends BaseGrpcConsumer {
    module = ChainModule.Evm;
    client;
    constructor(endpoint) {
        super(endpoint);
        this.client = new InjectiveEvmV1Query.QueryClientImpl(this.getGrpcWebImpl(endpoint));
    }
    async fetchAccount(ethAddress) {
        const request = InjectiveEvmV1Query.QueryAccountRequest.create();
        request.address = ethAddress;
        try {
            const response = await this.retry(() => this.client.Account(request, this.metadata));
            return ChainGrpcEvmTransformer.accountResponseToAccount(response);
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmAccount',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmAccount',
                contextModule: this.module,
            });
        }
    }
    async fetchCosmosAccount(ethAddress) {
        const request = InjectiveEvmV1Query.QueryCosmosAccountRequest.create();
        request.address = ethAddress;
        try {
            const response = await this.retry(() => this.client.CosmosAccount(request, this.metadata));
            return ChainGrpcEvmTransformer.cosmosAccountResponseToCosmosAccount(response);
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'CosmosAccount',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'CosmosAccount',
                contextModule: this.module,
            });
        }
    }
    async fetchValidatorAccount(consAddress) {
        const request = InjectiveEvmV1Query.QueryValidatorAccountRequest.create();
        request.consAddress = consAddress;
        try {
            const response = await this.retry(() => this.client.ValidatorAccount(request, this.metadata));
            return ChainGrpcEvmTransformer.validatorAccountResponseToValidatorAccount(response);
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmValidatorAccount',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmValidatorAccount',
                contextModule: this.module,
            });
        }
    }
    async fetchBalance(ethAddress) {
        const request = InjectiveEvmV1Query.QueryBalanceRequest.create();
        request.address = ethAddress;
        try {
            const response = await this.retry(() => this.client.Balance(request, this.metadata));
            return response.balance;
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmBalance',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmBalance',
                contextModule: this.module,
            });
        }
    }
    async fetchStorage(ethAddress, key) {
        const request = InjectiveEvmV1Query.QueryStorageRequest.create();
        request.address = ethAddress;
        request.key = key;
        try {
            const response = await this.retry(() => this.client.Storage(request, this.metadata));
            return response.value;
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmStorage',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmStorage',
                contextModule: this.module,
            });
        }
    }
    async fetchCode(ethAddress) {
        const request = InjectiveEvmV1Query.QueryCodeRequest.create();
        request.address = ethAddress;
        try {
            const response = await this.retry(() => this.client.Code(request, this.metadata));
            return response.code;
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmCode',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmCode',
                contextModule: this.module,
            });
        }
    }
    async fetchParams() {
        const request = InjectiveEvmV1Query.QueryParamsRequest.create();
        try {
            const response = await this.retry(() => this.client.Params(request, this.metadata));
            return ChainGrpcEvmTransformer.paramsResponseToParams(response);
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmParams',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmParams',
                contextModule: this.module,
            });
        }
    }
    async fetchBaseFee() {
        const request = InjectiveEvmV1Query.QueryBaseFeeRequest.create();
        try {
            const response = await this.retry(() => this.client.BaseFee(request, this.metadata));
            return response.baseFee;
        }
        catch (e) {
            if (e instanceof InjectiveEvmV1Query.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: grpcErrorCodeToErrorCode(e.code),
                    context: 'EvmBaseFee',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'EvmBaseFee',
                contextModule: this.module,
            });
        }
    }
}
